import React, { useState } from 'react';
import { EyeIcon, EyeOffIcon, FingerprintIcon, ScanFaceIcon, ShieldCheckIcon, UserRoundIcon, LockKeyholeIcon, ChevronRightIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
type Lang = 'EN' | 'SI' | 'TA';
const COPY: Record<Lang, {
  eyebrow: string;
  greeting: string;
  sub: string;
  nicLabel: string;
  nicPlaceholder: string;
  passwordLabel: string;
  passwordPlaceholder: string;
  forgot: string;
  signIn: string;
  continueWith: string;
  fingerprint: string;
  faceId: string;
  newToRDB: string;
  create: string;
  protected: string;
}> = {
  EN: {
    eyebrow: 'Sign in',
    greeting: 'Welcome back',
    sub: 'Use your NIC number and password to access your account safely.',
    nicLabel: 'NIC Number',
    nicPlaceholder: 'e.g. 198512345678',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    forgot: 'Forgot password?',
    signIn: 'Sign In Securely',
    continueWith: 'or continue with',
    fingerprint: 'Fingerprint',
    faceId: 'Face ID',
    newToRDB: 'New to RDB?',
    create: 'Create Account',
    protected: 'Protected by Central Bank of Sri Lanka'
  },
  SI: {
    eyebrow: 'ඇතුළු වන්න',
    greeting: 'සාදරයෙන් පිළිගනිමු',
    sub: 'ඔබගේ ජාතික හැඳුනුම්පත් අංකය සහ මුරපදය යොදා ආරක්ෂිතව ඇතුළු වන්න.',
    nicLabel: 'ජා.හැ. අංකය',
    nicPlaceholder: 'උදා: 198512345678',
    passwordLabel: 'මුරපදය',
    passwordPlaceholder: 'ඔබගේ මුරපදය ඇතුළු කරන්න',
    forgot: 'මුරපදය අමතකද?',
    signIn: 'ආරක්ෂිතව ඇතුළු වන්න',
    continueWith: 'හෝ භාවිත කරන්න',
    fingerprint: 'ඇඟිලි සලකුණ',
    faceId: 'මුහුණු හඳුනාගැනීම',
    newToRDB: 'RDB හි අලුත්ද?',
    create: 'ගිණුමක් සාදන්න',
    protected: 'ශ්‍රී ලංකා මහ බැංකුව මගින් ආරක්ෂා කරන ලදී'
  },
  TA: {
    eyebrow: 'உள்நுழைய',
    greeting: 'மீண்டும் வரவேற்கிறோம்',
    sub: 'உங்கள் தே.அ.அ. எண் மற்றும் கடவுச்சொல்லை பாதுகாப்பாக பயன்படுத்தவும்.',
    nicLabel: 'தே.அ.அ. எண்',
    nicPlaceholder: 'எ.கா. 198512345678',
    passwordLabel: 'கடவுச்சொல்',
    passwordPlaceholder: 'உங்கள் கடவுச்சொல்லை உள்ளிடவும்',
    forgot: 'கடவுச்சொல் மறந்துவிட்டதா?',
    signIn: 'பாதுகாப்பாக உள்நுழையவும்',
    continueWith: 'அல்லது தொடரவும்',
    fingerprint: 'கைரேகை',
    faceId: 'முக அடையாளம்',
    newToRDB: 'RDB-க்கு புதியவரா?',
    create: 'கணக்கை உருவாக்கவும்',
    protected: 'இலங்கை மத்திய வங்கியால் பாதுகாக்கப்பட்டது'
  }
};
const LANG_LABELS: Record<Lang, {
  code: string;
  native: string;
}> = {
  EN: {
    code: 'EN',
    native: 'English'
  },
  SI: {
    code: 'සි',
    native: 'සිංහල'
  },
  TA: {
    code: 'த',
    native: 'தமிழ்'
  }
};
export function LoginScreen({
  onForgot,
  onCreateAccount,
  onSignIn
}: {
  onForgot?: () => void;
  onCreateAccount?: () => void;
  onSignIn?: () => void;
} = {}) {
  const [nic, setNic] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [lang, setLang] = useState<Lang>('EN');
  const t = COPY[lang];
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F8F9FA'
  }} data-id="element-462">
      <StatusBar data-id="element-463" />

      {/* Top: logo + language pill */}
      <header className="flex items-center justify-between px-5 pt-2 pb-4" data-id="element-464">
        <img src="https://cdn.magicpatterns.com/uploads/1RovM3XUej3fgFALTqXTFt/1.png" alt="Regional Development Bank" style={{
        height: 60,
        width: 'auto'
      }} className="block" data-id="element-465" />
        <div role="tablist" aria-label="Language" className="flex items-center gap-0.5 p-1 rounded-full" style={{
        backgroundColor: '#E5EAF0'
      }} data-id="element-466">
          {(['EN', 'SI', 'TA'] as Lang[]).map(l => {
          const active = l === lang;
          return <button key={l} type="button" role="tab" aria-selected={active} onClick={() => setLang(l)} className="rounded-full transition-all" style={{
            minWidth: 38,
            height: 32,
            padding: '0 10px',
            backgroundColor: active ? '#FFFFFF' : 'transparent',
            color: active ? '#009AC7' : '#475569',
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: '0.01em',
            boxShadow: active ? '0 2px 6px rgba(15,23,42,0.08)' : 'none'
          }} data-id="element-467">
                {LANG_LABELS[l].code}
              </button>;
        })}
        </div>
      </header>

      <div className="flex-1 px-6 pb-3 flex flex-col" data-id="element-468">
        <div className="mt-1 mb-5" data-id="element-469">
          <div style={{
          fontSize: 12.5,
          fontWeight: 700,
          color: '#009AC7',
          letterSpacing: '0.08em',
          textTransform: 'uppercase'
        }} data-id="element-470">
            {t.eyebrow}
          </div>
          <h1 className="mt-1.5 tracking-tight" style={{
          fontSize: 30,
          fontWeight: 800,
          color: '#0F172A',
          lineHeight: 1.15,
          letterSpacing: '-0.02em'
        }} data-id="element-471">
            {t.greeting}
          </h1>
          <p className="mt-2" style={{
          fontSize: 14,
          color: '#525252',
          lineHeight: 1.6
        }} data-id="element-472">
            {t.sub}
          </p>
        </div>

        <form className="flex flex-col gap-3.5" onSubmit={e => {
        e.preventDefault();
        onSignIn?.();
      }} data-id="element-473">
          <Field label={t.nicLabel} icon={<UserRoundIcon size={20} style={{
          color: '#009AC7'
        }} data-id="element-475" />} data-id="element-474">
            <input type="text" inputMode="text" autoComplete="username" value={nic} onChange={e => setNic(e.target.value)} placeholder={t.nicPlaceholder} className="flex-1 bg-transparent outline-none" style={{
            fontSize: 16,
            color: '#0F172A',
            minHeight: 56,
            fontWeight: 500
          }} data-id="element-476" />
          </Field>

          <Field label={t.passwordLabel} icon={<LockKeyholeIcon size={20} style={{
          color: '#009AC7'
        }} data-id="element-478" />} data-id="element-477">
            <input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t.passwordPlaceholder} className="flex-1 min-w-0 bg-transparent outline-none" style={{
            fontSize: 16,
            color: '#0F172A',
            minHeight: 56,
            fontWeight: 500
          }} data-id="element-479" />
            <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="shrink-0 ml-auto flex items-center justify-center rounded-lg transition-colors active:bg-neutral-100 -mr-1" style={{
            width: 44,
            height: 44
          }} data-id="element-480">
              {showPassword ? <EyeOffIcon size={22} style={{
              color: '#525252'
            }} data-id="element-481" /> : <EyeIcon size={22} style={{
              color: '#525252'
            }} data-id="element-482" />}
            </button>
          </Field>

          <button type="button" onClick={onForgot} className="self-end inline-flex items-center gap-1 px-2 -mt-1" style={{
          minHeight: 36,
          color: '#009AC7',
          fontSize: 13,
          fontWeight: 700
        }} data-id="element-483">
            {t.forgot}
            <ChevronRightIcon size={15} data-id="element-484" />
          </button>

          <button type="submit" className="w-full rounded-2xl text-white font-semibold transition-all active:scale-[0.99]" style={{
          backgroundColor: '#009AC7',
          minHeight: 58,
          fontSize: 16,
          boxShadow: '0 14px 30px -10px rgba(0, 154, 199, 0.6)',
          letterSpacing: '0.01em'
        }} data-id="element-485">
            {t.signIn}
          </button>
        </form>

        <div className="flex items-center gap-3 my-4" data-id="element-486">
          <div className="flex-1 h-px" style={{
          backgroundColor: '#E5E5E5'
        }} data-id="element-487" />
          <span style={{
          fontSize: 11.5,
          color: '#7A7A7A',
          fontWeight: 500,
          letterSpacing: '0.04em'
        }} data-id="element-488">
            {t.continueWith}
          </span>
          <div className="flex-1 h-px" style={{
          backgroundColor: '#E5E5E5'
        }} data-id="element-489" />
        </div>

        <div className="grid grid-cols-2 gap-3" data-id="element-490">
          <button type="button" className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-white transition-all active:scale-[0.98]" style={{
          minHeight: 72,
          border: '1.5px solid #E5EAF0',
          fontSize: 12.5,
          fontWeight: 700,
          color: '#0F172A'
        }} aria-label={t.fingerprint} data-id="element-491">
            <FingerprintIcon size={26} style={{
            color: '#009AC7'
          }} data-id="element-492" />
            {t.fingerprint}
          </button>
          <button type="button" className="flex flex-col items-center justify-center gap-1 rounded-2xl bg-white transition-all active:scale-[0.98]" style={{
          minHeight: 72,
          border: '1.5px solid #E5EAF0',
          fontSize: 12.5,
          fontWeight: 700,
          color: '#0F172A'
        }} aria-label={t.faceId} data-id="element-493">
            <ScanFaceIcon size={26} style={{
            color: '#009AC7'
          }} data-id="element-494" />
            {t.faceId}
          </button>
        </div>
      </div>

      <div className="px-6 pt-3 pb-5 bg-white" style={{
      borderTop: '1px solid #EAEAEA'
    }} data-id="element-495">
        <div className="flex items-center justify-center gap-2 mb-3" data-id="element-496">
          <ShieldCheckIcon size={15} style={{
          color: '#009AC7'
        }} data-id="element-497" />
          <span style={{
          fontSize: 11.5,
          color: '#525252',
          fontWeight: 500
        }} data-id="element-498">
            {t.protected}
          </span>
        </div>
        <div className="flex items-center justify-center gap-2" data-id="element-499">
          <span style={{
          fontSize: 13.5,
          color: '#525252'
        }} data-id="element-500">
            {t.newToRDB}
          </span>
          <button type="button" onClick={onCreateAccount} className="px-2" style={{
          minHeight: 44,
          color: '#009AC7',
          fontSize: 14,
          fontWeight: 800
        }} data-id="element-501">
            {t.create}
          </button>
        </div>
      </div>
      <div className="flex justify-center pb-2 bg-white" data-id="element-502">
        <div className="w-[120px] h-[5px] rounded-full bg-neutral-900/80" aria-hidden="true" data-id="element-503" />
      </div>
    </div>;
}
function Field({
  label,
  icon,
  children
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-504">
      <div className="mb-2" style={{
      fontSize: 12.5,
      fontWeight: 700,
      color: '#0F172A',
      letterSpacing: '0.02em'
    }} data-id="element-505">
        {label}
      </div>
      <div className="flex items-center gap-3 px-4 rounded-2xl bg-white transition-colors focus-within:border-[#009AC7]" style={{
      border: '1.5px solid #E5EAF0',
      minHeight: 60,
      boxShadow: '0 1px 2px rgba(15,23,42,0.03)'
    }} data-id="element-506">
        <div className="shrink-0" data-id="element-507">{icon}</div>
        {children}
      </div>
    </label>;
}